import { defineStore } from 'pinia';

import type { SpecialNodeError } from '../types/workflow';

export const useWorkflowValidation = defineStore('bvWorkflowValidation', {
  state: () => ({
    // Track validation errors for each node
    nodeErrors: {} as Record<string | number, Record<string, string>>,
    specialNodesErrors: [] as SpecialNodeError[],
  }),

  getters: {
    // Get errors for a specific node
    getNodeErrors: (state) => (nodeId: string | number) => {
      return state.nodeErrors[nodeId] || {};
    },

    // Check if a node has any errors
    hasNodeErrors: (state) => (nodeId: string | number) => {
      const errors = state.nodeErrors[nodeId];
      const hasValidationErrors = Boolean(errors && Object.keys(errors).length > 0);
      const hasSpecialError = state.specialNodesErrors.some((entry) => entry.errorNodeId === nodeId);
      return hasValidationErrors || hasSpecialError;
    },

    // Check if a specific node is flagged as unavailable (premium step on a lower plan)
    isNodeUnavailable: (state) => (nodeId: string | number) => {
      return state.specialNodesErrors.some((entry) => String(entry.errorNodeId) === String(nodeId));
    },

    // Get all nodes with validation or server-reported (special) errors
    getNodesWithErrors: (state) => {
      const validationNodeIds = Object.keys(state.nodeErrors).filter(
        (nodeId) => Object.keys(state.nodeErrors[nodeId]).length > 0,
      );
      const specialNodeIds = state.specialNodesErrors.map((entry) => entry.errorNodeId);
      return [...new Set([...validationNodeIds, ...specialNodeIds])];
    },

    // Get total error count across all nodes
    getTotalErrorCount: (state) => {
      return Object.values(state.nodeErrors).reduce((total, nodeErrors) => {
        return total + Object.keys(nodeErrors).length;
      }, 0);
    },
  },

  actions: {
    /**
     * Update validation errors for a specific node and field
     */
    updateNodeFieldError(nodeId: string | number, fieldKey: string, error: string | null) {
      if (!this.nodeErrors[nodeId]) {
        this.nodeErrors[nodeId] = {};
      }

      if (error) {
        this.nodeErrors[nodeId][fieldKey] = error;
      } else {
        delete this.nodeErrors[nodeId][fieldKey];
        // Clean up empty node error objects
        if (Object.keys(this.nodeErrors[nodeId]).length === 0) {
          delete this.nodeErrors[nodeId];
        }
      }
    },

    /**
     * Clear all errors for a specific node
     */
    clearNodeErrors(nodeId: string | number) {
      delete this.nodeErrors[nodeId];
    },

    /**
     * Clear all errors across all nodes
     */
    clearAllErrors() {
      this.nodeErrors = {};
    },

    setSpecialNodesErrors(errors: SpecialNodeError[] | null | undefined) {
      this.specialNodesErrors = Array.isArray(errors) ? errors : [];
    },

    /**
     * Validate a specific node and update errors
     */
    validateNode(nodeId: string | number, fields: any[], values: Record<string, any>) {
      // Import validation function dynamically to avoid circular dependencies
      import('@bv/components/workflow/utils/form-validation').then(({ validateForm }) => {
        // First, clear errors for fields that are hidden
        if (this.nodeErrors[nodeId]) {
          fields.forEach((field) => {
            // Check if field should be shown
            let shouldShow = true;
            if (field.show !== undefined) {
              if (typeof field.show === 'function') {
                shouldShow = field.show(values);
              } else {
                shouldShow = field.show !== false;
              }
            }

            // Clear error if field is hidden
            if (!shouldShow && this.nodeErrors[nodeId][field.key]) {
              this.updateNodeFieldError(nodeId, field.key, null);
            }
          });
        }

        const result = validateForm(values, fields);

        if (result.isValid) {
          this.clearNodeErrors(nodeId);
        } else {
          // Clear existing errors for this node
          this.clearNodeErrors(nodeId);

          // Add new errors
          result.errors.forEach((error) => {
            // Extract field key from error message (assuming format: "Field Label: Error message")
            const colonIndex = error.indexOf(':');
            if (colonIndex > 0) {
              const fieldLabel = error.substring(0, colonIndex).trim();
              const errorMessage = error.substring(colonIndex + 1).trim();

              // Find the field key by label
              const field = fields.find((f) => f.label === fieldLabel);
              if (field) {
                this.updateNodeFieldError(nodeId, field.key, errorMessage);
              }
            }
          });
        }
      });
    },
  },
});
